import {BrandConfig} from "./BrandConfig";

/**
 * Network Funding, exactly as production renders today.
 *
 * A permanent safety net, not a development convenience: the brand now arrives from the server,
 * and a missing or malformed block would otherwise leave the footer blank on every page.
 * Falling back to the platform's own details always produces a legal, correctly styled page.
 */
export const DEFAULT_BRAND: BrandConfig = {
    slug: "network-funding",
    displayName: "Network Funding, LP",
    relationshipLine: "Powered by Network Funding, LP.",
    legalEntity: "Network Funding, L.P.",
    nmls: "2297",
    primaryColor: "#55D5F5",
    logoUrl: "",
    termsUrl: "https://nflp.com/terms-of-use/",
    licensingUrl: "https://nflp.com/licenses/",
    privacyUrl: "https://nflp.com/privacy-policy/",
    isDefault: true,
    // Mirrors exactly what the backend returns for the seeded network-funding row, so the fallback
    // and the live path render identically. linkOnWhite is production's #00AED9, pinned by an
    // override (changelog 2.8) so existing borrowers see no change; derived.linkOnWhite is what the
    // algorithm alone would give (#338093, which passes AA where #00AED9 does not).
    theme: {
        raw: "#55D5F5",
        surface: "#55D5F5",
        onSurface: "#222222",
        linkOnWhite: "#00AED9",
        tint: "#F1FCFE",
        rawContrastOnWhite: 1.72,
        onSurfaceContrast: 9.26,
        bodyTextOnSurfacePasses: true,
        suggestedSurface: null,
        linkAdjusted: true,
        derived: {
            surface: "#55D5F5",
            onSurface: "#222222",
            linkOnWhite: "#338093",
            tint: "#F1FCFE"
        }
    }
};

const ELEMENT_ID = "simpl-brand-config";

/**
 * Resolves a logo URL for display.
 *
 * Locally stored logos are saved as a root-relative path so the stored value stays portable
 * across environments. The page itself is served by nginx, but /brand-assets/** is routed to the
 * backend by the ALB's default rule, so a root-relative path resolves against the right service
 * when the two share an origin — and not when the frontend runs on its own, as on the dev server.
 * Anything starting with "/" is therefore resolved against the backend explicitly. Absolute URLs
 * (S3, or a CDN) are returned untouched.
 */
export function resolveLogoUrl(logoUrl: string): string {
    if (!logoUrl) return "";
    if (/^https?:\/\//i.test(logoUrl)) return logoUrl;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return logoUrl.indexOf("/") === 0 ? `${base}${logoUrl}` : logoUrl;
}

export function readBrandConfig(): BrandConfig {
    try {
        const el = document.getElementById(ELEMENT_ID);
        if (!el || !el.textContent) return DEFAULT_BRAND;

        const raw = el.textContent.trim();
        // The dev server serves the template unrendered, so an un-substituted expression can
        // reach the browser verbatim. Treat that, and the empty object, as "no brand supplied".
        if (!raw || raw.indexOf("${") === 0 || raw === "{}") return DEFAULT_BRAND;

        const parsed = JSON.parse(raw) as Partial<BrandConfig>;
        const merged: BrandConfig = {...DEFAULT_BRAND, ...stripEmpty(parsed)};
        // A brand row without a usable theme is worse than no brand at all — it would paint an
        // undefined surface. Keep the defaults rather than render something broken.
        if (!merged.theme || !merged.theme.surface) merged.theme = DEFAULT_BRAND.theme;
        return merged;
    } catch (e) {
        console.warn("Brand config could not be read; falling back to the default brand.", e);
        return DEFAULT_BRAND;
    }
}

function stripEmpty(brand: Partial<BrandConfig>): Partial<BrandConfig> {
    const out: any = {};
    Object.keys(brand).forEach(key => {
        const value = (brand as any)[key];
        // logoUrl is legitimately empty until a logo is uploaded; everything else falls back.
        if (value !== null && value !== undefined && (value !== "" || key === "logoUrl")) {
            out[key] = value;
        }
    });
    return out;
}

/**
 * Publishes the brand as CSS custom properties so stylesheets can use the brand without any
 * component having to thread colors through props.
 */
export function applyBrandCssVariables(brand: BrandConfig): void {
    const root = document.documentElement;
    const t = brand.theme;
    root.style.setProperty("--brand-surface", t.surface);
    root.style.setProperty("--brand-on-surface", t.onSurface);
    root.style.setProperty("--brand-link", t.linkOnWhite);
    root.style.setProperty("--brand-link-hover", t.raw);
    root.style.setProperty("--brand-tint", t.tint);
    root.style.setProperty("--brand-raw", t.raw);
}
